/**
 * Share message (Slack's "Share message" / forward; contracts messageExtras.ts ShareMessageRequest): which messages
 * can be shared, where to (conversations I can post in, most recent first, filtered by name), and how a shared message
 * describes its source ("Bo Chen · #design").
 */
import { MAX_SHARE_DESTINATIONS, type Message, type SharedMessage } from "@bundle/contracts";
import { messageUrl } from "./shell.ts";
import { channelTitle } from "./sidebar.ts";
import { memberName, type WorkspaceCache } from "./workspace.ts";

export type ShareDestination = { channelId: string; title: string; isDm: boolean; isPrivate: boolean };

/** Sent, not deleted (a pending or failed send can't be shared). */
export function canShare(m: Message): boolean {
  return !m.deleted && m.seq > 0;
}

const fold = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/** Conversations I can post in (member, not archived), most recently active first, matching `query` by name. */
export function shareTargets(cache: WorkspaceCache, query: string, limit = 50): ShareDestination[] {
  const q = fold(query.trim().replace(/^[#@]/, ""));
  const out: (ShareDestination & { at: number })[] = [];
  for (const c of Object.values(cache.channels)) {
    if (c.archived || cache.memberships[c.id] === undefined) continue;
    const isDm = c.kind === "dm" || c.kind === "group_dm";
    const title = isDm ? channelTitle(cache, c.id) : c.name;
    if (q !== "" && !fold(title).includes(q)) continue;
    out.push({ channelId: c.id, title, isDm, isPrivate: c.kind === "private", at: c.lastMessageAt ?? c.createdAt });
  }
  out.sort((a, b) => b.at - a.at || (a.title < b.title ? -1 : a.title > b.title ? 1 : 0));
  return out.slice(0, limit).map(({ at: _at, ...t }) => t);
}

export type SharedSource = {
  authorName: string;
  /** "#design", a DM's people, or "" when I can't see that conversation. */
  where: string;
  /** Opens the original in context (absent when I can't see it). */
  url?: string;
};

/** How a shared message names where it came from. */
export function sharedSource(shared: SharedMessage, cache: WorkspaceCache): SharedSource {
  const c = cache.channels[shared.channelId];
  const src: SharedSource = { authorName: memberName(cache, shared.authorId), where: "" };
  if (c === undefined) return src;
  src.where = c.kind === "dm" || c.kind === "group_dm" ? channelTitle(cache, c.id) : `#${c.name}`;
  src.url = messageUrl(cache.workspace.slug, shared.channelId, shared.messageId, shared.threadRootId);
  return src;
}

/**
 * Picking destinations (Slack's forward dialog takes several): tapping a conversation adds it (in the order picked) or
 * removes it when already picked; past `max` nothing is added.
 */
export function toggleShareDestination(selected: readonly string[], channelId: string, max: number = MAX_SHARE_DESTINATIONS): string[] {
  if (selected.includes(channelId)) return selected.filter((id) => id !== channelId);
  return selected.length >= max ? [...selected] : [...selected, channelId];
}
